import get from "lodash.get"
import set from "lodash/set.js"
import { formatCurrency } from "./currencyUtils.mjs"
import { formatDate, formatDateTime } from "./dateUtils.mjs"
import { formatPercentage } from "./numberUtils.mjs"
import { isString, labelFromName } from "./stringUtils.mjs"

/** Prefix for per-field metadata objects stored beside each field key (e.g. `props_email` holds props for `email`). */
export const PROPS_ = "props_"

/**
 * Returns the metadata object (`props_*`) for a field path on a form/model object.
 *
 * @param {object} obj - Root object (e.g. form state).
 * @param {string} field - Field path (`addresses[0].street`, `name`, etc.).
 * @returns {object} Props object or `{}` when missing.
 */
export function getFieldProps(obj, field) {
  if (!obj || !field) return {}

  const fieldPropsPath = getFieldPropsPath(field)
  return get(obj, fieldPropsPath, {}) || {}
}

/**
 * Maps a logical field path to its `props_` storage path (see {@link PROPS_}).
 *
 * @param {string} fieldName - Dot/bracket path to the field.
 * @returns {string|undefined} Lodash path to the props object, or `undefined` if `fieldName` is empty.
 *
 * @example
 * getFieldPropsPath("addresses[0].address1") // "addresses[0].props_address1"
 */
export function getFieldPropsPath(fieldName) {
  if (!fieldName) return

  // "addresses[0].address1" => "addresses[0].props_address1"
  // "obj.nestedArray[0].nestedField.field" => "obj.nestedArray[0].nestedField.props_field"
  if (fieldName.includes(".")) {
    const nestedField = fieldName.split(".").pop() // "address1" // "field"
    return `${fieldName.replace(nestedField, "")}${PROPS_}${nestedField}`
  }

  return PROPS_ + fieldName
}

export const setMin = (obj, fieldNames, min) => setFieldProp(obj, fieldNames, "min", min)
export const setMax = (obj, fieldNames, max) => setFieldProp(obj, fieldNames, "max", max)
export const setType = (obj, fieldNames, type) => setFieldProp(obj, fieldNames, "type", type)
export const setLabel = (obj, fieldNames, label) => setFieldProp(obj, fieldNames, "label", label)
export const setValue = (obj, fieldNames, value) => setFieldProp(obj, fieldNames, "value", value)
export const setQuery = (obj, fieldNames, query) => setFieldProp(obj, fieldNames, "query", query)
export const setSelect = (obj, fieldNames, select) => setFieldProp(obj, fieldNames, "select", select)
export const setWarning = (obj, fieldNames, warning) => setFieldProp(obj, fieldNames, "warningFormat", warning)
export const setError = (obj, fieldNames, error) => setFieldProp(obj, fieldNames, "errorFormat", error)
export const setHidden = (obj, fieldNames, isHidden) => setFieldProp(obj, fieldNames, "hidden", isHidden)
export const setTouched = (obj, fieldNames, isTouched) => setFieldProp(obj, fieldNames, "touched", isTouched)
export const setDisabled = (obj, fieldNames, isDisabled) => setFieldProp(obj, fieldNames, "disabled", isDisabled)
export const setReadOnly = (obj, fieldNames, isReadOnly) => setFieldProp(obj, fieldNames, "readOnly", isReadOnly)
export const setMultiple = (obj, fieldNames, isMultiple) => setFieldProp(obj, fieldNames, "multiple", isMultiple)
export const setMandatory = (obj, fieldNames, isMandatory) => setFieldProp(obj, fieldNames, "mandatory", isMandatory)
export const setShowMandatoryHint = (obj, fieldNames, showMandatoryHint) => setFieldProp(obj, fieldNames, "showMandatoryHint", showMandatoryHint)
export const setColProps = (obj, fieldNames, colProps) => setFieldProp(obj, fieldNames, "colProps", colProps)
export const setAdvancedSearch = (obj, fieldNames, advancedSearch) => setFieldProp(obj, fieldNames, "advancedSearch", advancedSearch)
export const setCreatable = (obj, fieldNames, creatable) => setFieldProp(obj, fieldNames, "creatable", creatable)
export const setColumns = (obj, fieldNames, columns) => setFieldProp(obj, fieldNames, "columns", columns)

export const getWarning = (obj, fieldName) => getFieldProp(obj, fieldName, "warningFormat")
export const getError = (obj, fieldName) => getFieldProp(obj, fieldName, "errorFormat")
export const isHidden = (obj, fieldName) => getFieldProp(obj, fieldName, "hidden")
export const isTouched = (obj, fieldName) => getFieldProp(obj, fieldName, "touched")
export const isDisabled = (obj, fieldName) => getFieldProp(obj, fieldName, "disabled")
export const isMandatory = (obj, fieldName) => getFieldProp(obj, fieldName, "mandatory")

/**
 * @param {object} obj - Model object using `props_` metadata.
 * @returns {boolean|undefined} `true` if any field has `errorFormat` or an empty mandatory+touched field; otherwise falsy.
 */
export function hasErrors(obj) {
  const _hasErrors = navigateProps_(obj, (_obj, fieldName, fieldProps) => {
    // Format error
    if (fieldProps?.errorFormat) return true

    // Empty mandatory field
    if (isEmpty(_obj[fieldName]) && fieldProps?.mandatory && fieldProps?.touched) return true
  })
  return _hasErrors
}

/**
 * @param {{ value: *, min: * }} params
 * @returns {boolean} Whether `value` is strictly below `min` (and `min` is not empty per {@link isEmpty}).
 */
export function isLessThanMin({ value, min }) {
  return !isEmpty(min) && value < min
}

/**
 * @param {{ value: *, max: * }} params
 * @returns {boolean} Whether `value` is strictly above `max` (and `max` is not empty per {@link isEmpty}).
 */
export function isGreaterThanMax({ value, max }) {
  return !isEmpty(max) && value > max
}

/** @internal */
function checkLessThanMin({ min, value, type, currency, locale }) {
  if (!type && isString(value)) type = "text"

  const isText = ["text", "password", "textarea"].includes(type) && typeof value === "string"
  if (value) value = isText ? value.trim().length : value

  if (!isLessThanMin({ value, min })) return

  return `${isText ? "Length must" : "Must"} be ≥ ${format({ value: min, type, currency, locale })}`
}

/** @internal */
function checkGreaterThanMax({ max, value, type, currency, locale }) {
  if (!type && isString(value)) type = "text"

  const isText = ["text", "password", "textarea"].includes(type) && typeof value === "string"
  if (value) value = isText ? value.trim().length : value

  if (!isGreaterThanMax({ value, max })) return

  return `${isText ? "Length must" : "Must"} be ≤ ${format({ value: max, type, currency, locale })}`
}

// In some cases, instanceof Date might not return true even if it is an actual Date object
/** @internal */
function isDate(value) {
  return value instanceof Date || Object.prototype.toString.call(value) === "[object Date]"
}

/** @internal Formats bounds and display values for validation messages. */
function format({ value, type, currency, locale }) {
  if (type === "percentage") return formatPercentage(value, locale)
  if (type === "currency") return formatCurrency(value, locale, currency)

  if (["date", "datetime"].includes(type) || isDate(value)) {
    if (type === "datetime") return formatDateTime(value, locale)
    return formatDate(value, locale)
  }

  return value
}

/**
 * Collects `warningFormat` messages from all `props_` nodes.
 *
 * @param {object} obj - Model object.
 * @returns {Array<string|{ modelFieldPath: string, value: string }>|undefined} Deduplicated warnings, or `undefined` if none.
 */
export function getWarnings(obj) {
  const warnings = []

  // Get "obj" fields warning by priority from "props_" (warningFormat only)
  navigateProps_(obj, (_obj, fieldName, fieldProps, fieldPath) => {
    // Warning if field has "warningFormat" prop
    if (fieldProps?.warningFormat) {
      warnings.push(toWarning(fieldPath, fieldProps.warningFormat))
    }
  })

  if (!warnings.length) return
  return Array.from(new Set(warnings))
}

/**
 * Builds validation errors from `props_` metadata and optional `documents.*` errors.
 *
 * @param {object} obj - Model object.
 * @param {{ loc: function, locale: string }} ctx - `loc` is the i18n interpolation function; `locale` is passed to formatters.
 * @returns {Array<{ modelFieldPath: string, value: string }>|undefined} Deduplicated errors, or `undefined` if none.
 */
export function getErrors(obj, { loc, locale }) {
  const errors = []

  // Retrieve the errors from "obj.documents" so that you can scroll to them
  // This is to support the hack/workaround done in <DocumentComponent/> when <DocumentsComponent/> is injected as a card in a page
  // See <DocumentComponent/>: data-model-field-path={`documents.${type}`} in basikon-client
  if (typeof obj?.documents === "object" && !Array.isArray(obj?.documents)) {
    for (const documentType in obj.documents) {
      if (!obj.documents?.[documentType]?.errorFormat) continue

      errors.push({
        modelFieldPath: `documents.${documentType}`,
        value: obj.documents[documentType].errorFormat,
      })
    }
  }

  // Get "obj" fields error by priority from "props_" (1. errorFormat, 2. min/max, 3. mandatory)
  navigateProps_(obj, (_obj, fieldName, fieldProps, fieldPath) => {
    const value = _obj[fieldName]

    // 1. Error if field has "errorFormat" prop
    if (fieldProps?.errorFormat) {
      errors.push(toError(fieldPath, fieldProps.errorFormat))
    }
    // 2. Error if field is not empty && (< min || > max)
    else if (!isEmpty(value)) {
      let typeChecked = fieldProps.type
      if (!typeChecked && isString(value)) typeChecked = "text"
      const isText = ["text", "password", "textarea"].includes(typeChecked) && typeof value === "string"
      const valueChecked = value && isText ? value.trim().length : value
      const label = fieldProps.label || loc(labelFromName(fieldName))

      if (isLessThanMin({ value: valueChecked, min: fieldProps.min })) {
        const formattedMin = format({ value: fieldProps.min, type: typeChecked, currency: fieldProps.currency, locale })
        errors.push(toError(fieldPath, loc(isText ? "$ length must be ≥ $" : "$ must be ≥ $", label, formattedMin)))
      }

      if (isGreaterThanMax({ value: valueChecked, max: fieldProps.max })) {
        const formattedMax = format({ value: fieldProps.max, type: typeChecked, currency: fieldProps.currency, locale })
        errors.push(toError(fieldPath, loc(isText ? "$ length must be ≤ $" : "$ must be ≤ $", label, formattedMax)))
      }
    }
    // 3. Error if field is empty && mandatory && touched
    else if (isEmpty(value) && fieldProps.mandatory && fieldProps.touched) {
      const label = fieldProps.label || loc(labelFromName(fieldName))
      errors.push(toError(fieldPath, loc("$ is mandatory", label)))
    }
  })

  if (!errors.length) return
  return Array.from(new Set(errors))
}

/** @internal */
function toWarning(fieldPath, warningMessage) {
  if (fieldPath) return { modelFieldPath: fieldPath, value: warningMessage }
  return warningMessage
}

/** @internal */
function toError(fieldPath, errorMessage) {
  if (fieldPath) return { modelFieldPath: fieldPath, value: errorMessage }
  return errorMessage
}

/**
 * Validates the model, logs issues, may touch mandatory fields, and sets `obj.errors`.
 *
 * Mutates `obj` when mandatory fields are auto-touched. Returns a list of error descriptors or `null` when nothing to report.
 *
 * @param {object} obj - Model object with `props_` metadata.
 * @param {function(object): void} [onSetState] - Called with `obj` when state should refresh (touched fields or collected errors).
 * @param {{ skipWorkflowErrors?: boolean }} [options]
 * @param {boolean} [options.skipWorkflowErrors=false] - If true, ignores `errorFormat` on fields marked `isWorkflowTransitionError`.
 * @returns {Array|boolean|null} Error list, `true`-like legacy cases, or `null` when no errors.
 */
export function checkErrors(obj, onSetState, { skipWorkflowErrors = false } = {}) {
  let hasTouched, hasEmptyMandatoryFields, hasInvalidMinMaxFields
  const errors = []

  navigateProps_(obj, (_obj, fieldName, fieldProps, fieldPath) => {
    const value = _obj[fieldName]

    // Check whether there are any "errorFormat" present in the fields of the "obj"
    if (fieldProps?.errorFormat && (!skipWorkflowErrors || !fieldProps.isWorkflowTransitionError)) {
      console.error(`checkErrors(): Error on field "${fieldName}": ${fieldProps.errorFormat} (path: ${fieldPath || "."})`)
      errors.push(toFieldPath(fieldPath, fieldProps.errorFormat))
    }

    // Check whether there are empty "mandatory" fields
    if (isEmpty(value) && fieldProps?.mandatory && fieldProps?.touched) {
      console.error(`checkErrors(): Empty mandatory field "${fieldName}" (path: ${fieldPath || "."})`)
      hasEmptyMandatoryFields = true
      errors.push(toError(fieldPath))
    }

    // Check whether there are invalid "min"/"max" fields
    const minError = checkLessThanMin({ min: fieldProps.min, value, type: fieldProps.type, currency: obj.currency })
    const maxError = checkGreaterThanMax({ max: fieldProps.max, value, type: fieldProps.type, currency: obj.currency })
    if (minError || maxError) {
      console.error(`checkErrors(): Invalid value in field "${fieldName}": ${minError || maxError} (path: ${fieldPath || "."})`)
      hasInvalidMinMaxFields = true
    }

    // Touch empty "mandatory" fields to show the errors
    if (fieldProps?.mandatory && isEmpty(value) && !fieldProps?.touched) {
      console.log(`checkErrors(): Touched mandatory empty field "${fieldName}" (path: ${fieldPath || "."})`)
      fieldProps.touched = true
      hasTouched = true
      errors.push(toError(fieldPath))
    }
  })

  obj.errors = errors

  if (hasTouched || errors.length) {
    if (onSetState) onSetState(obj)
    return errors
  }

  const hasErrors = hasTouched || hasEmptyMandatoryFields || hasInvalidMinMaxFields || errors.length > 0
  return hasErrors ? errors : null
}

/** @internal */
function getFieldProp(obj, fieldName, propName) {
  if (!obj || !fieldName) return

  const fieldPropsPath = getFieldPropsPath(fieldName)
  return get(obj, `${fieldPropsPath}.${propName}`)
}

/**
 * Sets a single metadata property on one or more fields (under `props_*`).
 *
 * @param {object} obj - Model object (mutated; may be created if missing).
 * @param {string|string[]} fieldNames - Field path(s).
 * @param {string} propName - Metadata key (`min`, `label`, `errorFormat`, etc.).
 * @param {*} propValue - Value to assign.
 * @returns {object} The same `obj` reference.
 */
export function setFieldProp(obj, fieldNames, propName, propValue) {
  if (!Array.isArray(fieldNames)) fieldNames = [fieldNames]

  for (const fieldName of fieldNames) {
    if (!fieldName) continue

    const fieldPropsPath = getFieldPropsPath(fieldName)

    obj = obj || {}
    set(obj, `${fieldPropsPath}.${propName}`, propValue)
  }
  return obj
}

/**
 * Merges several metadata keys onto one or more fields at once.
 *
 * @param {object} obj - Model object (mutated).
 * @param {string|string[]} fieldNames - Field path(s).
 * @param {Object<string, *>} props - Map of prop names to values.
 * @returns {object} The same `obj` reference.
 */
export function setFieldProps(obj, fieldNames, props) {
  if (!Array.isArray(fieldNames)) fieldNames = [fieldNames]

  for (const fieldName of fieldNames) {
    if (!fieldName) continue

    const fieldPropsPath = getFieldPropsPath(fieldName)

    obj = obj || {}
    for (const propName in props || {}) set(obj, `${fieldPropsPath}.${propName}`, props[propName])
  }
  return obj
}

/** @internal Walks the tree, invoking `propsFunction` on each `props_*` node. */
function navigateProps_(obj, propsFunction, path) {
  for (const key in obj) {
    const fieldPath = toFieldPath(path, key)

    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (key.startsWith(PROPS_)) {
        const fieldName = key.replace(PROPS_, "")
        const fieldProps = obj[key] || {}
        const stop = propsFunction(obj, fieldName, fieldProps, fieldPath)
        if (stop) return stop
      }
      // If the property is an object, recursively navigate it
      else if (obj[key] && typeof obj[key] === "object" && !isReactComponent(obj, key)) {
        const stop = navigateProps_(obj[key], propsFunction, fieldPath)
        if (stop) return stop
      }
    }
  }
}

// Not an ideal solution, but works for now
// React.isValidElement() would be better for forward compatibility, but that’s tricky since this runs on both client and server.
/** @internal */
function isReactComponent(obj, key) {
  return (
    typeof obj[key].$$typeof === "symbol" || // react v16
    key.startsWith("__react") || // react v18
    key.startsWith("_valueTracker") || // react v18
    key.startsWith("_wrapperState") // react v18
  )
}

/** @internal Builds a model field path string for arrays and nested keys. */
function toFieldPath(path, key) {
  const isIndex = Number(key) >= 0
  const isProps_ = key.startsWith(PROPS_)
  const field = isProps_ && key.replace(PROPS_, "")

  if (isIndex && path) return `${path}[${key}]`
  if (isProps_ && path) return `${path}.${field}`
  if (isIndex && !path) return key
  if (isProps_ && !path) return field
  if (path) return `${path}.${key}`
  return key
}

/**
 * @param {*} value - Field value (string, number, array, plain object, etc.).
 * @returns {boolean} Whether the value counts as empty for validation (including `""`, `[]`, `{}`; `0` is not empty).
 */
export function isEmpty(value) {
  return (
    (!value && value !== 0) ||
    (Array.isArray(value) && value.length === 0) ||
    (value.constructor.name === "Object" && Object.keys(value).length === 0)
  ) // Other objects like dates can have `typeof value === "object"` so we need to check the constructor
}

/**
 * @param {object} prevObj - Previous model snapshot.
 * @param {object} obj - Current model.
 * @param {string} field - Field name (top-level key on `obj`).
 * @returns {boolean} Whether the field value changed (date-safe for `Date` instances).
 */
export function hasChanged(prevObj, obj, field) {
  if (!prevObj || !obj) return false
  if (isDate(obj[field])) return obj[field].getTime() !== prevObj[field]?.getTime()
  return obj[field] !== prevObj[field]
}
